'use client';

import { forwardRef } from 'react';
import { Cake } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldHelp } from '@/features/guided-help';
import {
  TournamentAgeRestrictionFields,
  type TournamentAgeRestrictionFieldsHandle,
} from '@/components/tournament/shared/TournamentAgeRestrictionFields';
import type {
  TournamentAgeDraftSlice,
  TournamentAgeRestrictionPatch,
} from '@/utils/tournament-age';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export type AgeRestrictionFieldProps = {
  value: TournamentAgeDraftSlice;
  onChange: (patch: TournamentAgeRestrictionPatch) => void;
  helpText: string;
  wrapInCard?: boolean;
  onApiPayloadValidityChange?: (valid: boolean) => void;
};

/**
 * Misma presentación que el bloque de edad en {@link TeamConfigStep} (pádel): título con icono y campos compartidos.
 */
export const AgeRestrictionField = forwardRef<
  TournamentAgeRestrictionFieldsHandle,
  AgeRestrictionFieldProps
>(function AgeRestrictionField(
  {
    value,
    onChange,
    helpText,
    wrapInCard = true,
    onApiPayloadValidityChange,
  },
  ref,
) {
  const fields = (
    <TournamentAgeRestrictionFields
      ref={ref}
      value={value}
      onChange={onChange}
      helpText={helpText}
      onApiPayloadValidityChange={onApiPayloadValidityChange}
    />
  );

  if (!wrapInCard) {
    return (
      <div
        className={wizardBlockShellClassName}
        data-guide-id="wizard.team.ageRestriction"
      >
        <div className="mb-3 flex items-center gap-2 text-lg font-semibold">
          <Cake className="h-5 w-5 text-blue-600" />
          Restricción de edad
          <FieldHelp topicId="wizard.team.ageRestriction" />
        </div>
        {fields}
      </div>
    );
  }

  return (
    <Card
      className={wizardBlockCardClassName}
      data-guide-id="wizard.team.ageRestriction"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cake className="h-5 w-5 text-blue-600" />
          Restricción de edad
          <FieldHelp topicId="wizard.team.ageRestriction" />
        </CardTitle>
      </CardHeader>
      <CardContent>{fields}</CardContent>
    </Card>
  );
});
